/**
 * Utility functions for handling persona data in URLs
 * Centralizes persona ID and summary extraction logic
 */

/**
 * Extracts persona information from a decoded URL query
 * @param {string} decodedQuery - The decoded URL query string
 * @returns {Object} Object containing personaId and summary
 */
export const extractPersonaFromURL = (decodedQuery) => {
  if (!decodedQuery) {
    return { personaId: null, summary: null };
  }

  const personaIdMatch = decodedQuery.match(/data-persona-id="([^"]+)"/);
  const summaryMatch = decodedQuery.match(/data-summary="([^"]+)"/);

  return {
    personaId: personaIdMatch ? personaIdMatch[1] : null,
    summary: summaryMatch ? summaryMatch[1] : null,
  };
};

/**
 * Gets the current URL query parameter
 * @returns {string|null} The query parameter value or null
 */
export const getCurrentURLQuery = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('query');
};

/**
 * Checks if a persona ID exists in the user's personas list
 * @param {string} personaId - The persona ID to check
 * @param {Array} userPersonas - Array of user personas
 * @returns {Object|null} The matching persona or null
 */
export const findPersonaById = (personaId, userPersonas) => {
  if (!personaId || !Array.isArray(userPersonas)) {
    return null;
  }
  return userPersonas.find(p => p._id === personaId) || null;
};

/**
 * Handles persona selection based on URL and stored persona
 * @param {string} urlPersonaId - Persona ID from URL
 * @param {string} storedPersonaId - Persona ID from localStorage
 * @param {Array} userPersonas - Array of user personas
 * @returns {Object} Result object with action and persona
 */
export const resolvePersonaSelection = (urlPersonaId, storedPersonaId, userPersonas) => {
  // No URL persona - use stored persona
  if (!urlPersonaId) {
    const storedPersona = findPersonaById(storedPersonaId, userPersonas);
    return {
      action: storedPersona ? 'restore' : 'none',
      persona: storedPersona,
    };
  }

  // URL persona matches stored persona
  if (urlPersonaId === storedPersonaId) {
    const persona = findPersonaById(urlPersonaId, userPersonas);
    return {
      action: persona ? 'restore' : 'clear',
      persona,
    };
  }

  // URL has different persona
  const urlPersona = findPersonaById(urlPersonaId, userPersonas);
  return {
    action: urlPersona ? 'switch' : 'generate',
    persona: urlPersona,
  };
};
